/**
 * UNIFARM APR Calculator Utilities
 * 
 * These functions calculate APR for Root Pools and Child Pools
 * based on TVL, reward amounts, and pool durations.
 */

import { useBondSize } from "../hooks/useBondSize";
import { tokenAmount } from "./tokenAmount";

/**
 * Calculate Root Pool APR
 * 
 * @param {number} rewardAmount - Total reward tokens allocated
 * @param {string|Date} poolEndDate - ISO date string or Date object for pool end
 * @param {number} totalTVL - Total Value Locked across all child pools (in same token units as rewards)
 * @param {number} [gemsFeePct=0.5] - GEMS platform fee percentage (default 0.5%)
 * @returns {number} APR as a percentage (e.g., 25.5 for 25.5% APR)
 */
export function calculateRootPoolAPR(rewardAmount, poolEndDate, totalTVL, gemsFeePct = 0.5) {
    if (!rewardAmount || !poolEndDate || !totalTVL || totalTVL <= 0) {
        return 0;
    }

    // // Calculate remaining duration in days
    // const now = new Date();
    // const endDate = new Date(poolEndDate);
    // const durationDays = Math.max(0, (endDate - now) / (1000 * 60 * 60 * 24));

    // if (durationDays <= 0) {
    //     return 0; // Pool has ended
    // }

    // // Net rewards after GEMS fee
    // const netRewards = rewardAmount * (1 - gemsFeePct / 100);

    // // Annual rewards (extrapolate based on duration)
    // const annualRewards = (netRewards / durationDays) * 365;

    // // APR = (Annual Rewards / Total TVL) * 100
    // const apr = (annualRewards / totalTVL) * 100;

    return 0

    // return parseFloat(apr.toFixed(2));
}


/**
 * Calculate Child Pool APR
 * 
 * @param {number} rewardAmount - Total reward tokens in root pool
 * @param {string|Date} poolEndDate - ISO date string or Date object for pool end
 * @param {number} childTVL - TVL in this specific child pool
 * @param {number} totalTVL - Total TVL across all child pools
 * @param {number} creatorFeePercent - Creator's fee percentage (e.g., 1 for 1%)
 * @param {number} [gemsFeePct=0.5] - GEMS platform fee percentage (default 0.5%)
 * @returns {number} APR as a percentage (e.g., 25.5 for 25.5% APR)
 */
export function calculateChildPoolAPR(
    rewardAmount,
    poolEndDate,
    childTVL,
    totalTVL,
    creatorFeePercent = 0,
    gemsFeePct = 0.5
) {
    if (!rewardAmount || !poolEndDate || !childTVL || !totalTVL || totalTVL <= 0) {
        return 0;
    }

    // Calculate remaining duration in days
    const now = new Date();
    const endDate = new Date(poolEndDate);
    const durationDays = Math.max(0, (endDate - now) / (1000 * 60 * 60 * 24));

    if (durationDays <= 0) {
        return 0; // Pool has ended
    }

    // This child pool's share of total rewards (proportional to TVL)
    const poolShare = childTVL / totalTVL;
    const poolRewards = rewardAmount * poolShare;

    // Deduct fees: GEMS fee first, then creator fee
    const afterGemsFee = poolRewards * (1 - gemsFeePct / 100);
    const userRewards = afterGemsFee * (1 - creatorFeePercent / 100);

    // Annual rewards for users
    const annualUserRewards = (userRewards / durationDays) * 365;

    // APR = (Annual User Rewards / Child Pool TVL) * 100
    const apr = (annualUserRewards / childTVL) * 100;

    return parseFloat(apr.toFixed(2));
}


/**
 * Calculate APR with lock multiplier
 * 
 * @param {number} baseAPR - Base APR without lock
 * @param {number} lockMultiplier - Lock multiplier (e.g., 1.5 for 50% boost)
 * @returns {number} Boosted APR
 */
export function calculateLockedAPR(baseAPR, lockMultiplier = 1) {
    if (!baseAPR || lockMultiplier <= 0) {
        return 0;
    }

    return parseFloat((baseAPR * lockMultiplier).toFixed(2));
}


/**
 * Calculate effective TVL with lock multipliers
 * (Used in reward allocation formula from PRD)
 * 
 * @param {number} baseTVL - Base TVL amount
 * @param {number} lockMultiplier - Lock multiplier (default 1)
 * @returns {number} Effective TVL
 */
export function calculateEffectiveTVL(baseTVL, lockMultiplier = 1) {
    return baseTVL * lockMultiplier;
}


/**
 * Calculate user's share of pool rewards after all fees
 * 
 * @param {number} totalRewards - Total rewards claimable
 * @param {number} gemsFeePct - GEMS fee percentage
 * @param {number} creatorFeePct - Creator fee percentage
 * @returns {Object} Breakdown of rewards { gems, creator, user }
 */
export function calculateRewardSplit(totalRewards, gemsFeePct = 0.5, creatorFeePct = 0) {
    const gemsFee = totalRewards * (gemsFeePct / 100);
    const afterGemsFee = totalRewards - gemsFee;
    const creatorFee = afterGemsFee * (creatorFeePct / 100);
    const userRewards = afterGemsFee - creatorFee;

    return {
        gems: parseFloat(gemsFee.toFixed(6)),
        creator: parseFloat(creatorFee.toFixed(6)),
        user: parseFloat(userRewards.toFixed(6))
    };
}


/**
 * Example Usage with your data:
 */

// Example 1: Calculate Root Pool APR
const rootAPR = calculateRootPoolAPR(
    1000000,                          // rewardAmount
    "2025-10-30T19:00:00.000Z",      // poolEndDate
    5000000                           // totalTVL
);
// console.log("Root Pool APR:", rootAPR + "%");


// Example 2: Calculate Child Pool APR
const childAPR = calculateChildPoolAPR(
    1000000,                          // rewardAmount (from root pool)
    "2025-10-30T19:00:00.000Z",      // poolEndDate (from root pool)
    500000,                           // childTVL (this child pool's TVL)
    5000000,                          // totalTVL (all child pools)
    1                                 // creatorFeePercent
);
// console.log("Child Pool APR:", childAPR + "%");


// Example 3: Calculate reward split
const rewardSplit = calculateRewardSplit(1000, 0.5, 1);
// console.log("Reward Split:", rewardSplit);
// Output: { gems: 5, creator: 9.95, user: 985.05 }





/**
 * Calculate APR based on reward amount, TVL, and pool end date.
 * Ensures no Infinity or NaN results.
 *
 * @param {number} rewardAmount - The total reward amount.
 * @param {number} tvlAmount - The total value locked.
 * @param {string|Date} poolEndDate - The end date of the pool.
 * @returns {number} - The calculated APR (0 if invalid or expired).
 */
export function calculateAPR(rewardAmount, poolEndDate, tvlAmount) {
    
    return 0;
}
export function calculateAPR2(totalRewards, tvlWei, totalBondsWei, poolEndDate, poolStartDate = null) {
    
    // Convert wei to ether (divide by 1e18)
    let amount = tokenAmount(totalRewards, 18);
    const tvlEther = Number(tokenAmount(tvlWei, 18).formatted);
    const bondsEther = Number(tokenAmount(totalBondsWei, 18).formatted);

    // totalRewards is already in human readable format
    const rewards = Number(totalRewards);

    // Calculate duration in years
    let durationSeconds;
    if (poolStartDate) {
        // Use provided start date
        const startDate = new Date(poolStartDate);
        const endDate = new Date(poolEndDate);
        durationSeconds = Math.max(0, (endDate - startDate) / 1000);
    } else {
        // Use current time as start
        const now = new Date();
        const endDate = new Date(poolEndDate);
        durationSeconds = Math.max(0, (endDate - now) / 1000);
    }

    const durationYears = durationSeconds / (365.25 * 24 * 60 * 60);

    // Validate inputs
    if (durationYears === 0 || tvlEther + bondsEther <= 0 || rewards <= 0) {
        return 0;
    }

    // Step-by-step calculation
    // console.log('=== APR Calculation Steps ===', poolStartDate);
    // console.log('Step 1 - Inputs:');
    // console.log('  - Total Rewards:', rewards);
    // console.log('  - TVL (in ether):', tvlEther);
    // console.log('  - Total Bonds (in ether):', bondsEther);
    // console.log('  - Duration (years):', durationYears.toFixed(6));
    // console.log('  - Pool Start Date:', poolStartDate ? new Date(poolStartDate).toISOString() : 'Now (current time)');
    // console.log('  - Pool End Date:', new Date(poolEndDate).toISOString());

    // Step 2: Calculate sum of TVL + Bonds
    const tvlPlusBonds = tvlEther + bondsEther;
    // console.log('\nStep 2 - Sum TVL + Bonds:');
    // console.log('  - TVL + Bonds =', tvlEther, '+', bondsEther, '=', tvlPlusBonds);

    // Step 3: Divide rewards by (TVL + Bonds)
    const rewardRatio = rewards / tvlPlusBonds;
    // console.log('\nStep 3 - Rewards Ratio:');
    // console.log('  -', rewards, '/', tvlPlusBonds, '=', rewardRatio);

    // Step 4: Multiply by 100 to get percentage
    const rewardPercentage = rewardRatio * 100;
    // console.log('\nStep 4 - Convert to Percentage:');
    // console.log('  -', rewardRatio, '* 100 =', rewardPercentage, '%');

    // Step 5: Divide by duration in years
    const apr = rewardPercentage / durationYears;
    // console.log('\nStep 5 - Annualize (divide by years):');
    // console.log('  -', rewardPercentage, '/', durationYears.toFixed(6), '=', apr);

    // Ensure result is a valid number and round to 2 decimals
    let finalAPR = Number.isFinite(apr) ? apr : 0;
    finalAPR = parseFloat(finalAPR.toFixed(2));

    // console.log('\nFinal APR:', finalAPR, '%');
    // console.log('===========================\n');

    return finalAPR;
}

// Example usage:
// calculateAPR(1000, '1000000000000000000', '500000000000000000', '2025-10-31T19:00:00.000Z')
// totalRewards: 1000 (human readable)
// tvlWei: '1000000000000000000' (1 ether in wei)
// totalBondsWei: '500000000000000000' (0.5 ether in wei)
// poolEndDate: '2025-10-31T19:00:00.000Z'

export function calculateChildPoolAPR2(tvlIWei, bondIWei, tvlJWei, bondJWei, parentApr) {
    // Convert wei to numbers using tokenAmount
    const tvlI_num = Number(tokenAmount(tvlIWei, 18).formatted);
    const bondI_num = Number(tokenAmount(bondIWei, 18).formatted);
    const tvlJ_num = Number(tokenAmount(tvlJWei, 18).formatted);
    const bondJ_num = Number(tokenAmount(bondJWei, 18).formatted);

    // Validate inputs
    if (tvlI_num <= 0 || bondI_num <= 0 || tvlJ_num <= 0 || bondJ_num <= 0) {
        console.warn('Invalid inputs: all TVL and Bond values must be greater than 0');
        return 0;
    }

    // Step-by-step calculation
    // console.log('=== Child Pool APR Calculation ===');
    // console.log('Formula: (TVL_i^1.05 * sqrt(Bond_i)) / (TVL_j^1.05 * sqrt(Bond_j))');
    // console.log('\nInputs:');
    // console.log('  - TVL_i:', tvlI_num);
    // console.log('  - Bond_i:', bondI_num);
    // console.log('  - TVL_j:', tvlJ_num);
    // console.log('  - Bond_j:', bondJ_num);

    // Numerator: TVL_i^1.05 * sqrt(Bond_i)
    const tvlI_power = Math.pow(tvlI_num, 1.05);
    // console.log('\nNumerator Calculation:');
    // console.log('  Step 1 - TVL_i^1.05:');
    // console.log('    -', tvlI_num, '^1.05 =', tvlI_power);

    const sqrtBondI = Math.sqrt(bondI_num);
    // console.log('  Step 2 - sqrt(Bond_i):');
    // console.log('    - sqrt(', bondI_num, ') =', sqrtBondI);

    const numerator = tvlI_power * sqrtBondI;
    // console.log('  Step 3 - Multiply:');
    // console.log('    -', tvlI_power, '*', sqrtBondI, '=', numerator);

    // Denominator: TVL_j^1.05 * sqrt(Bond_j)
    const tvlJ_power = Math.pow(tvlJ_num, 1.05);
    // console.log('\nDenominator Calculation:');
    // console.log('  Step 1 - TVL_j^1.05:');
    // console.log('    -', tvlJ_num, '^1.05 =', tvlJ_power);

    const sqrtBondJ = Math.sqrt(bondJ_num);
    // console.log('  Step 2 - sqrt(Bond_j):');
    // console.log('    - sqrt(', bondJ_num, ') =', sqrtBondJ);

    const denominator = tvlJ_power * sqrtBondJ;
    // console.log('  Step 3 - Multiply:');
    // console.log('    -', tvlJ_power, '*', sqrtBondJ, '=', denominator);

    // Final calculation
    const childPoolAPR = numerator / denominator;
    // console.log('\nFinal Calculation:');
    // console.log('  -', numerator, '/', denominator, '=', childPoolAPR);

    // Round to 2 decimals
    let finalChildPoolAPR = Number.isFinite(childPoolAPR) ? childPoolAPR : 0;
    finalChildPoolAPR = parseFloat(finalChildPoolAPR.toFixed(2));

    // console.log('\nChild Pool APR:', finalChildPoolAPR);
    finalChildPoolAPR = parseFloat(finalChildPoolAPR) * parseFloat(parentApr);
    // console.log('\nChild Pool APR: with parent apr', finalChildPoolAPR);
    // console.log('===================================\n');

    return finalChildPoolAPR;
}

export const fun=(bondWei, tvlWei, rawParentTvl, rawParentBond, rawParentApr)=>{


    // console.log(bondWei, tvlWei,rawParentTvl,rawParentBond,rawParentApr, 'bondWei, tvlWei, fun');

}

// Example usage:
// calculateChildPoolAPR(
//   tvlIWei = '100000000000000000000',  // 100 in wei
//   bondIWei = '50000000000000000000',  // 50 in wei
//   tvlJWei = '200000000000000000000',  // 200 in wei
//   bondJWei = '75000000000000000000'   // 75 in wei
// )